import CarbonCreditService from '@/_services/CarbonCreditService';

const state = {
  farmLocation: null,
  // Plot just created from PlotLocation, to merge into AddPlotTree on return
  newlyAddedPlot: null,
  // Users returned from /carbon-credit/tree-mapping/option/user
  treeMappingUsers: [],
  // Cache users by role_id to avoid refetch when value already selected (e.g. after restoring draft)
  treeMappingUsersByRole: {},
  // Draft form data when user goes to Choose Farm Location from Add Plot Tree (restored on return)
  addPlotTreeDraft: null,
  // Cached response for GET /user/membership/user-role (avoids refetch when roles already loaded)
  userMembershipRoleResponse: null,
  // True when user saved from FarmLocation (edit mode: triggers plot search on return)
  farmLocationJustSaved: false,
  // Edit mode: selected plots when navigating to PlotLocation (restored on return)
  pendingEditSelectedPlots: null,
  /** { polygons: Array<Array<{lat,lng}>>, projectTitle: string } */
  projectKmlBoundary: null,
};

const getters = {
  getFarmLocation: (state) => state.farmLocation,
  getNewlyAddedPlot: (state) => state.newlyAddedPlot,
  getTreeMappingUsers: (state) => state.treeMappingUsers,
  getTreeMappingUsersForRole: (state) => (roleId) => (roleId ? state.treeMappingUsersByRole[roleId] : null),
  getAddPlotTreeDraft: (state) => state.addPlotTreeDraft,
  getCachedUserMembershipRoleResponse: (state) => state.userMembershipRoleResponse,
  getFarmLocationJustSaved: (state) => state.farmLocationJustSaved,
  getPendingEditSelectedPlots: (state) => state.pendingEditSelectedPlots,
  getProjectKmlBoundary: (state) => state.projectKmlBoundary,
};

const mutations = {
  SET_FARM_LOCATION(state, payload) {
    state.farmLocation = payload;
  },
  CLEAR_FARM_LOCATION(state) {
    state.farmLocation = null;
  },
  SET_NEWLY_ADDED_PLOT(state, plot) {
    state.newlyAddedPlot = plot;
  },
  CLEAR_NEWLY_ADDED_PLOT(state) {
    state.newlyAddedPlot = null;
  },
  SET_TREE_MAPPING_USERS(state, users) {
    state.treeMappingUsers = users;
  },
  SET_TREE_MAPPING_USERS_FOR_ROLE(state, { roleId, users }) {
    if (roleId) {
      state.treeMappingUsersByRole = { ...state.treeMappingUsersByRole, [roleId]: users };
    }
  },
  SET_ADD_PLOT_TREE_DRAFT(state, payload) {
    state.addPlotTreeDraft = payload;
  },
  CLEAR_ADD_PLOT_TREE_DRAFT(state) {
    state.addPlotTreeDraft = null;
  },
  SET_FARM_LOCATION_JUST_SAVED(state, value) {
    state.farmLocationJustSaved = !!value;
  },
  SET_PENDING_EDIT_SELECTED_PLOTS(state, payload) {
    state.pendingEditSelectedPlots = payload;
  },
  SET_USER_MEMBERSHIP_ROLE_RESPONSE(state, payload) {
    state.userMembershipRoleResponse = payload;
  },
  SET_PROJECT_KML_BOUNDARY(state, payload) {
    state.projectKmlBoundary = payload;
  },
  CLEAR_PROJECT_KML_BOUNDARY(state) {
    state.projectKmlBoundary = null;
  },
};

const actions = {
  setFarmLocation({ commit }, location) {
    commit('SET_FARM_LOCATION', location);
  },
  clearFarmLocation({ commit }) {
    commit('CLEAR_FARM_LOCATION');
  },
  setNewlyAddedPlot({ commit }, plot) {
    commit('SET_NEWLY_ADDED_PLOT', plot);
  },
  clearNewlyAddedPlot({ commit }) {
    commit('CLEAR_NEWLY_ADDED_PLOT');
  },
  setAddPlotTreeDraft({ commit }, draft) {
    commit('SET_ADD_PLOT_TREE_DRAFT', draft);
  },
  clearAddPlotTreeDraft({ commit }) {
    commit('CLEAR_ADD_PLOT_TREE_DRAFT');
  },
  setFarmLocationJustSaved({ commit }, value = true) {
    commit('SET_FARM_LOCATION_JUST_SAVED', value);
  },
  clearFarmLocationJustSaved({ commit }) {
    commit('SET_FARM_LOCATION_JUST_SAVED', false);
  },
  setPendingEditSelectedPlots({ commit }, payload) {
    commit('SET_PENDING_EDIT_SELECTED_PLOTS', payload);
  },
  clearPendingEditSelectedPlots({ commit }) {
    commit('SET_PENDING_EDIT_SELECTED_PLOTS', null);
  },
  setProjectKmlBoundary({ commit }, payload) {
    commit('SET_PROJECT_KML_BOUNDARY', payload);
  },
  clearProjectKmlBoundary({ commit }) {
    commit('CLEAR_PROJECT_KML_BOUNDARY');
  },
  // Users for plot-tree mapping assignees, filtered by role (cached by role_id to avoid refetch)
  async fetchTreeMappingUsersByRole({ state, commit }, params = {}) {
    const roleId = params.role_id;
    const cached = roleId ? state.treeMappingUsersByRole[roleId] : null;
    if (cached) {
      return { success: true, data: cached };
    }
    const res = await CarbonCreditService.getUsersByRole(params);
    const users = res?.data || [];
    commit('SET_TREE_MAPPING_USERS', users);
    if (roleId) {
      commit('SET_TREE_MAPPING_USERS_FOR_ROLE', { roleId, users });
    }
    return { success: true, data: users };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}; 